import type { Optional } from '@festina-life/core'
import type {
  ConstructorArgument,
  ConstructorCall,
} from '@festina-life/fplus-monaco'

import type { CollectionType } from '../fplusExpression/types'

/**
 * Transforms a value or an array of values to F+ code format.
 *
 * We explicitly choose not to escape/unescape the string values in order to keep the implementation
 * simple and to not be opinionated about the user's intent. Users should escape the strings themselves
 * while editing. Not doing so removes support for the simple editor and can always be fixed through the
 * FPlus editor. More context on this can be found here
 * https://github.com/festina-life/frontend-general/pull/1860#discussion_r1560637613
 *
 * @param {string[]} value - The value or array of values to transform.
 * @param {CollectionType} [collectionType] - The collection type ('list' or 'object') to construct.
 * @returns {string} The transformed value in F+ code format.
 */
export const transformLiteralValuesToFPlusCode = ({
  value,
  collectionType,
}: {
  value: string[]
  collectionType?: Optional<CollectionType>
}): string => {
  if (collectionType) {
    return serialiseCollection({ value, collectionType })
  }

  return value.at(0) ?? ''
}

const formatArgument = (argument: ConstructorArgument) =>
  `${argument.name} = ${argument.value}`

export const transformConstructorValuesToFPlusCode = ({
  value,
  collectionType,
}: {
  value: ConstructorCall[]
  collectionType?: Optional<CollectionType>
}): string => {
  const fPlusConstructorCalls = value.map(
    (constructorCall) =>
      `${constructorCall.name}(${constructorCall.arguments.map(formatArgument).join(', ')})`,
  )

  if (!collectionType) {
    return fPlusConstructorCalls.at(0) ?? ''
  }

  return serialiseCollection({ value: fPlusConstructorCalls, collectionType })
}

const serialiseCollection = ({
  value,
  collectionType,
}: {
  value: string[]
  collectionType: CollectionType
}) =>
  collectionType === 'list'
    ? `[ ${value.join(', ')} ]`
    : `{ ${value.join(', ')} }`
